import React from 'react';
import {
  Box,
  Chip,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { formatCurrency, formatDuration, formatDistance, formatDateTime } from '@/utils/formatters';
import { useIsMobile } from '@/hooks/use-mobile';

export interface TripData {
  uuid: string;
  sequenceNumber: number;
  shiftSequenceNumber: number;
  startAt: string;
  endAt: string;
  duration: number;
  totalAmount: number;
  subtractedTotalAmount: number;
  finalTotalAmount: number;
  extrasAmount: number;
  subtractedExtrasAmount: number;
  finalExtrasAmount: number;
  waitingTime: number;
  totalDistance: number;
  hiredDistance: number;
  onCallDistance: number;
  isCancelled: boolean;
  licenseNumber: string;
  driverNumber: number;
  paymentMethod: string;
  isFlatRate: boolean;
}

interface TripItemProps {
  trip: TripData;
}

const TripItem: React.FC<TripItemProps> = ({ trip }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <AccordionItem key={trip.uuid} value={trip.uuid}>
      <AccordionTrigger className="hover:no-underline">
        <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="subtitle2">
              {t('assignments.trip')} #{trip.sequenceNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(trip.startAt)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={formatCurrency(trip.finalTotalAmount)}
              color={trip.isCancelled ? 'error' : 'success'}
              size="small"
            />
            <Chip
              label={t(`assignments.paymentMethods.${trip.paymentMethod}`) || trip.paymentMethod}
              variant="outlined"
              size="small"
            />
            {trip.isCancelled && (
              <Chip
                label={t('assignments.cancelled')}
                color="error"
                size="small"
              />
            )}
          </Box>
        </Box>
      </AccordionTrigger>
      <AccordionContent>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.duration')}
              </Typography>
              <Typography variant="body2">
                {formatDuration(trip.duration)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.waitingTime')}
              </Typography>
              <Typography variant="body2">
                {formatDuration(trip.waitingTime)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.totalDistance')}
              </Typography>
              <Typography variant="body2">
                {formatDistance(trip.totalDistance)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.hiredDistance')}
              </Typography>
              <Typography variant="body2">
                {formatDistance(trip.hiredDistance)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.onCallDistance')}
              </Typography>
              <Typography variant="body2">
                {formatDistance(trip.onCallDistance)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.licenseNumber')}
              </Typography>
              <Typography variant="body2">
                {trip.licenseNumber}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.paymentMethod')}
              </Typography>
              <Typography variant="body2">
                {t(`assignments.paymentMethods.${trip.paymentMethod}`) || trip.paymentMethod}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('assignments.flatRate')}
              </Typography>
              <Typography variant="body2">
                {trip.isFlatRate ? t('common.yes') : t('common.no')}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {t('assignments.financialDetails')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('assignments.totalAmount')}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(trip.totalAmount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('assignments.subtractedAmount')}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(trip.subtractedTotalAmount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('assignments.finalAmount')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(trip.finalTotalAmount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('assignments.extrasAmount')}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(trip.extrasAmount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('assignments.subtractedExtras')}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(trip.subtractedExtrasAmount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('assignments.finalExtras')}
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(trip.finalExtrasAmount)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </AccordionContent>
    </AccordionItem>
  );
};

export default TripItem;
